/**
 * React Query Hook: Abandon Branch
 *
 * Discards an active branch. The file reverts to main content in the editor.
 * Only ACTIVE branches can be abandoned.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { graphqlRequest } from '@/services/deployment/client';
import { ABANDON_BRANCH_MUTATION } from '@/services/metadata_v1/queries';
import type {
  AbandonBranchResponse,
  AbandonBranchVariables,
  MetadataBranch,
} from '@/services/metadata_v1/types';
import { useAuthStoreV1 } from '@/stores/authStore_v1';

async function abandonBranch(
  variables: AbandonBranchVariables
): Promise<MetadataBranch> {
  const response = await graphqlRequest<AbandonBranchResponse>(
    ABANDON_BRANCH_MUTATION,
    variables
  );

  if (!response?.abandonBranch) {
    throw new Error('Abandon failed. Please try again.');
  }

  return response.abandonBranch;
}

export interface UseAbandonBranchOptions {
  onSuccess?: (data: MetadataBranch) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to abandon (discard) an active branch.
 *
 * Invalidates `activeBranch` and `userBranches` caches on success so the
 * editor reverts to main content.
 *
 * @param metadataItemId - The file's item ID, used to invalidate the activeBranch cache
 */
export function useAbandonBranch(
  metadataItemId?: string | null,
  options?: UseAbandonBranchOptions
): UseMutationResult<MetadataBranch, Error, { branchId: string }> {
  const queryClient = useQueryClient();
  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  const organizationId = selectedOrg?.id;

  return useMutation({
    mutationFn: ({ branchId }: { branchId: string }) =>
      abandonBranch({ input: { branchId } }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['activeBranch', metadataItemId ?? data.metadataItem?.id],
      });

      queryClient.invalidateQueries({
        queryKey: ['userBranches', organizationId],
      });

      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}
